import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import {
  Map, MapPinned, Trees, Building2, Utensils, Mountain, Hotel, ShoppingBag, ScrollText,
  Loader2, Sparkles, Plus, Trash2, Eye, EyeOff, ChevronRight, AlertCircle, Wand2, Printer,
} from "lucide-react";
import { toast } from "sonner";

const POI_ICON = {
  cellar: Building2, vineyard: Trees, view: Mountain, restaurant: Utensils,
  lodging: Hotel, shop: ShoppingBag, history: ScrollText, default: MapPinned,
};

const TYPE_META = {
  tourism_route: { fr: "Parcours œnotouristique", en: "Wine tourism route", icon: MapPinned },
  parcel: { fr: "Carte parcellaire", en: "Plot map", icon: Trees },
  print_360: { fr: "Affiche 360° (print)", en: "360° print poster", icon: Printer },
};

const AUDIENCES = [
  { key: "Découverte", label_fr: "Découverte", label_en: "Discovery" },
  { key: "Famille", label_fr: "Famille", label_en: "Family" },
  { key: "Expert", label_fr: "Expert", label_en: "Expert" },
  { key: "Dégustation", label_fr: "Dégustation", label_en: "Tasting" },
  { key: "Randonnée", label_fr: "Randonnée", label_en: "Hiking" },
];

export default function ProMapBuilder() {
  const { lang } = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ artifacts: [], has_producer: false });
  const [loading, setLoading] = useState(true);
  const [genOpen, setGenOpen] = useState(false);
  const [genType, setGenType] = useState("tourism_route");
  const [genForm, setGenForm] = useState({ audience: "Découverte", duration_min: 90, notes: "" });
  const [generating, setGenerating] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/pro/map-builder/artifacts");
      setData(d);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate("/login?next=/pro/map-builder");
      return;
    }
    refresh();
  }, [user]); // eslint-disable-line

  const openArtifact = async (id) => {
    setOpenId(id);
    setDetail(null);
    try {
      const { data: d } = await api.get(`/pro/map-builder/artifacts/${id}`);
      setDetail(d);
    } catch {
      toast.error(lang === "fr" ? "Chargement impossible" : "Could not load");
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data: d } = await api.post("/pro/map-builder/artifacts/generate", { type: genType, ...genForm });
      toast.success(lang === "fr" ? "Artefact généré ✨" : "Artifact generated ✨");
      setGenOpen(false);
      await refresh();
      openArtifact(d.id);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "premium_only") {
        toast.error(lang === "fr" ? "Réservé aux abonnés Pro" : "Pro subscribers only");
      } else if (detail?.error === "no_producer") {
        toast.error(lang === "fr" ? "Créez d'abord votre fiche domaine." : "Create domain profile first.");
      } else if (detail?.error === "insufficient_credits") {
        toast.error(lang === "fr" ? `Crédits insuffisants (${detail.cost} requis)` : `Not enough credits (${detail.cost} needed)`);
      } else {
        toast.error(lang === "fr" ? "Génération échouée" : "Generation failed");
      }
    } finally {
      setGenerating(false);
    }
  };

  const togglePublish = async (art) => {
    try {
      await api.put(`/pro/map-builder/artifacts/${art.id}`, { published: !art.published });
      toast.success(!art.published
        ? (lang === "fr" ? "Publié sur la fiche publique" : "Published on public profile")
        : (lang === "fr" ? "Caché du public" : "Hidden from public"));
      await refresh();
      if (openId === art.id) openArtifact(art.id);
    } catch {
      toast.error(lang === "fr" ? "Action impossible" : "Action failed");
    }
  };

  const remove = async (art) => {
    if (!confirm(lang === "fr" ? "Supprimer cet artefact ?" : "Delete this artifact?")) return;
    try {
      await api.delete(`/pro/map-builder/artifacts/${art.id}`);
      toast.success(lang === "fr" ? "Supprimé" : "Deleted");
      if (openId === art.id) { setOpenId(null); setDetail(null); }
      await refresh();
    } catch {
      toast.error(lang === "fr" ? "Suppression échouée" : "Delete failed");
    }
  };

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#E5276A]" /></div>;

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-12 md:py-16" data-testid="pro-map-builder-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="max-w-2xl">
          <span className="chip-pink">{lang === "fr" ? "Espace Pro · Sprint 9.4" : "Pro space · Sprint 9.4"}</span>
          <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">
            {lang === "fr" ? "Map Builder & Designer" : "Map Builder & Designer"}
          </h1>
          <p className="text-[#4A5568] mt-3">
            {lang === "fr"
              ? "Générez automatiquement vos parcours œnotouristiques, votre carte parcellaire et vos affiches 360° à partir de votre fiche domaine. 10 crédits par artefact."
              : "Auto-generate wine-tourism routes, plot maps and 360° printable posters from your domain profile. 10 credits per artifact."}
          </p>
        </div>
        <button
          data-testid="map-builder-new"
          onClick={() => setGenOpen(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661]"
        >
          <Plus className="w-4 h-4" /> {lang === "fr" ? "Nouvel artefact" : "New artifact"}
        </button>
      </div>

      {!data.has_producer && (
        <div className="mt-8 rounded-2xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-900 inline-flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {lang === "fr"
              ? "Créez d'abord votre fiche producteur via l'AI Onboarding."
              : "First create your producer profile via AI Onboarding."}
            <Link to="/pro/dashboard" className="ml-2 underline font-bold">{lang === "fr" ? "Aller à l'onboarding" : "Go to onboarding"}</Link>
          </span>
        </div>
      )}

      {/* Generator panel */}
      {genOpen && (
        <div className="mt-6 rounded-2xl border-2 border-[#E5276A]/30 bg-[#FFF8FB] p-5 md:p-7 space-y-4" data-testid="map-builder-gen-form">
          <div className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-[#E5276A]" />
            <h2 className="font-display text-xl text-[#1F3A5F]">{lang === "fr" ? "Nouveau · IA assistée" : "New · AI assisted"}</h2>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8B95A1]">{lang === "fr" ? "Type d'artefact" : "Artifact type"}</label>
            <div className="mt-2 grid sm:grid-cols-3 gap-2">
              {Object.entries(TYPE_META).map(([key, meta]) => {
                const Icon = meta.icon;
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={`map-type-${key}`}
                    onClick={() => setGenType(key)}
                    className={`px-3 py-3 rounded-xl text-left border transition-colors ${
                      genType === key ? "border-[#E5276A] bg-[#FCE4ED] text-[#E5276A]" : "border-[#1F3A5F]/15 text-[#1F3A5F] hover:border-[#E5276A]"
                    }`}
                  >
                    <Icon className="w-4 h-4 mb-1" />
                    <div className="text-sm font-bold">{lang === "fr" ? meta.fr : meta.en}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {genType === "tourism_route" && (
            <>
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8B95A1]">{lang === "fr" ? "Audience" : "Audience"}</label>
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="map-audience-group">
                  {AUDIENCES.map((a) => (
                    <button
                      key={a.key}
                      data-testid={`map-audience-${a.key}`}
                      type="button"
                      onClick={() => setGenForm({ ...genForm, audience: a.key })}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border ${
                        genForm.audience === a.key ? "border-[#E5276A] bg-[#FCE4ED] text-[#E5276A]" : "border-[#1F3A5F]/15 text-[#1F3A5F] hover:border-[#E5276A]"
                      }`}
                    >{lang === "fr" ? a.label_fr : a.label_en}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8B95A1]">{lang === "fr" ? "Durée cible (min)" : "Target duration (min)"}</label>
                <input
                  type="number"
                  min={30}
                  max={300}
                  value={genForm.duration_min}
                  onChange={(e) => setGenForm({ ...genForm, duration_min: parseInt(e.target.value) || 90 })}
                  className="mt-1 w-full sm:w-40 px-3 py-2 rounded-lg border border-[#1F3A5F]/15 text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]"
                />
              </div>
            </>
          )}

          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8B95A1]">{lang === "fr" ? "Notes libres" : "Free notes"}</label>
            <textarea
              data-testid="map-notes"
              value={genForm.notes}
              onChange={(e) => setGenForm({ ...genForm, notes: e.target.value })}
              placeholder={lang === "fr" ? "Spécificités, contraintes, focus particulier…" : "Specifics, constraints, focus…"}
              rows={3}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-[#1F3A5F]/15 text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]"
            />
          </div>

          <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#1F3A5F]/10">
            <button onClick={() => setGenOpen(false)} className="text-xs text-[#8B95A1] hover:text-[#1F3A5F] font-bold">{lang === "fr" ? "Annuler" : "Cancel"}</button>
            <button
              data-testid="map-generate"
              onClick={generate}
              disabled={generating || !data.has_producer}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661] disabled:opacity-50"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {lang === "fr" ? "Générer (10 crédits)" : "Generate (10 credits)"}
            </button>
          </div>
        </div>
      )}

      {/* Artifacts list */}
      <div className="mt-8 grid gap-4">
        {data.artifacts.length === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-[#1F3A5F]/20 bg-white p-8 text-center">
            <Map className="w-10 h-10 text-[#E5276A] mx-auto opacity-60" />
            <p className="mt-3 text-sm text-[#4A5568]">{lang === "fr" ? "Aucun artefact encore. Lancez votre première génération." : "No artifact yet. Generate your first one."}</p>
          </div>
        )}
        {data.artifacts.map((a) => {
          const meta = TYPE_META[a.type] || TYPE_META.tourism_route;
          const Icon = meta.icon;
          return (
            <div key={a.id} className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-5 hover:border-[#E5276A] transition-colors" data-testid={`map-artifact-${a.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <span className="p-2 rounded-lg bg-[#FCE4ED] text-[#E5276A]"><Icon className="w-5 h-5" /></span>
                  <div className="min-w-0">
                    <h3 className="font-display text-lg text-[#1F3A5F] truncate">{a.name}</h3>
                    <p className="text-xs text-[#8B95A1] mt-0.5">{lang === "fr" ? meta.fr : meta.en} · v{a.version || 1} {a.audience ? `· ${a.audience}` : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`map-publish-${a.id}`} onClick={() => togglePublish(a)} title={a.published ? (lang === "fr" ? "Dépublier" : "Unpublish") : (lang === "fr" ? "Publier" : "Publish")}
                    className={`p-2 rounded-lg ${a.published ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-[#FBF7F3] text-[#8B95A1] hover:bg-[#FCE4ED] hover:text-[#E5276A]"}`}>
                    {a.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button data-testid={`map-open-${a.id}`} onClick={() => openArtifact(a.id)} className="p-2 rounded-lg bg-[#FBF7F3] text-[#1F3A5F] hover:bg-[#FCE4ED] hover:text-[#E5276A]">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button data-testid={`map-delete-${a.id}`} onClick={() => remove(a)} className="p-2 rounded-lg bg-[#FBF7F3] text-[#8B95A1] hover:bg-red-50 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {openId === a.id && (
                <div className="mt-4 pt-4 border-t border-[#1F3A5F]/10" data-testid={`map-artifact-detail-${a.id}`}>
                  {!detail ? (
                    <div className="text-sm text-[#8B95A1] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {lang === "fr" ? "Chargement…" : "Loading…"}</div>
                  ) : (
                    <ArtifactView detail={detail} lang={lang} />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ArtifactView({ detail, lang }) {
  const p = detail.payload || {};
  if (detail.type === "tourism_route") {
    return (
      <div>
        {p.summary && <p className="text-sm text-[#1F3A5F] leading-relaxed italic mb-4">{p.summary}</p>}
        {p.total_min && (
          <p className="text-xs text-[#4A5568] mb-3">{lang === "fr" ? "Durée totale" : "Total duration"} : <b>{p.total_min} min</b>{p.recommended_season ? ` · ${p.recommended_season}` : ""}</p>
        )}
        <ol className="space-y-3">
          {(p.pois || []).map((poi, idx) => {
            const Icon = POI_ICON[poi.type] || POI_ICON.default;
            return (
              <li key={idx} className="rounded-xl border border-[#1F3A5F]/10 bg-[#FBF7F3] p-3 flex gap-3" data-testid={`map-poi-${idx}`}>
                <div className="flex flex-col items-center gap-1 shrink-0">
                  <span className="w-7 h-7 rounded-full bg-[#E5276A] text-white text-xs font-bold inline-flex items-center justify-center">{idx + 1}</span>
                  <Icon className="w-4 h-4 text-[#1F3A5F]" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <h4 className="font-bold text-[#1F3A5F]">{poi.title}</h4>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#8B95A1]">{poi.duration_min || ""} min · {poi.access || ""}</span>
                  </div>
                  <p className="text-sm text-[#4A5568] mt-1 leading-relaxed">{poi.description}</p>
                  {poi.tip && <p className="mt-1 text-xs text-[#E5276A] italic">💡 {poi.tip}</p>}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    );
  }
  if (detail.type === "parcel") {
    return (
      <div>
        {p.total_ha && <p className="text-xs text-[#4A5568] mb-3">{lang === "fr" ? "Surface totale" : "Total area"} : <b>{p.total_ha} ha</b></p>}
        <div className="grid sm:grid-cols-2 gap-3">
          {(p.parcels || []).map((par, idx) => (
            <div key={idx} className="rounded-xl border border-[#1F3A5F]/10 bg-[#FBF7F3] p-3 flex gap-3" data-testid={`map-parcel-${idx}`}>
              <span className="w-3 h-12 rounded-md shrink-0" style={{ background: par.color || "#1F3A5F" }} />
              <div className="min-w-0">
                <h4 className="font-bold text-[#1F3A5F]">{par.name}</h4>
                <p className="text-xs text-[#8B95A1]">{par.lieu_dit} · {par.area_ha} ha · {par.cepage}</p>
                <p className="text-xs text-[#4A5568] mt-1">{par.exposition} · {par.soil} · {par.age_vignes} {lang === "fr" ? "ans" : "yrs"}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (detail.type === "print_360") {
    return (
      <div>
        <h4 className="font-display text-2xl text-[#1F3A5F]">{p.headline}</h4>
        <p className="text-sm text-[#E5276A] mt-1">{p.subhead}</p>
        <div className="mt-4 grid sm:grid-cols-3 gap-3 text-xs">
          {p.history_caption && <div className="rounded-lg bg-[#FBF7F3] p-3"><b>{lang === "fr" ? "Histoire" : "History"}</b><br />{p.history_caption}</div>}
          {p.terroir_caption && <div className="rounded-lg bg-[#FBF7F3] p-3"><b>{lang === "fr" ? "Terroir" : "Terroir"}</b><br />{p.terroir_caption}</div>}
          {p.visit_caption && <div className="rounded-lg bg-[#FBF7F3] p-3"><b>{lang === "fr" ? "Visite" : "Visit"}</b><br />{p.visit_caption}</div>}
        </div>
        {p.qr_call_to_action && <p className="mt-3 text-xs italic text-[#4A5568]">QR CTA: {p.qr_call_to_action}</p>}
        {(p.formats || []).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {(p.formats || []).map((f) => <span key={f} className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-[#FCE4ED] text-[#E5276A]">{f}</span>)}
          </div>
        )}
        <p className="mt-3 text-[11px] text-[#8B95A1]">{lang === "fr" ? "Export PDF — à venir." : "PDF export — coming soon."}</p>
      </div>
    );
  }
  return <pre className="text-xs">{JSON.stringify(p, null, 2)}</pre>;
}
